/* One uploaded picture, in every size and format the site serves.
 *
 * WHY THIS EXISTS. The hero was a single URL in `background-image`. A CSS
 * background cannot carry a `srcset`, so a phone on a slow connection
 * downloaded the same 2400px photo a desktop did — for a 390px-wide screen.
 * Bassem's rule is explicit: mobile must not download the desktop image. That
 * means a real `<img>` with `srcset`, which means the stored value has to be
 * a set of sizes rather than one URL.
 *
 * WHERE THE SIZES COME FROM. The browser makes them, at upload time, from the
 * image the admin chose (`imageUpload.ts`). There is no image-resizing service
 * in this stack and adding one for two pictures would be a new thing to
 * operate; the browser already has the file decoded.
 *
 * NEVER UPSCALED. A 900px photo yields 800 only. Writing a 2400 entry that is
 * really a 900px image stretched would make `srcset` lie to the browser, and
 * the browser would pick it for a wide screen and show a blurry one.
 */

/** The widths the site serves, widest first. */
export const IMAGE_WIDTHS = [2400, 1440, 800] as const;

/** 5 MB — the bucket's own limit, and the worker rejects above it too. */
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

export const ACCEPTED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

export type ImageSet = {
  /** width → URL. WebP for browsers that take it. */
  webp: Record<string, string>;
  /** width → URL. The fallback every browser takes. */
  jpeg: Record<string, string>;
  /** The source's own size, for the aspect ratio and to avoid upscaling. */
  width: number;
  height: number;
};

/** A stored value may be an ImageSet, a legacy single URL, or nothing. */
export type StoredImage = ImageSet | string | null | undefined;

export function isImageSet(v: unknown): v is ImageSet {
  if (!v || typeof v !== "object") return false;
  const s = v as ImageSet;
  return !!s.jpeg && typeof s.jpeg === "object" && Object.keys(s.jpeg).length > 0;
}

/** The widths actually present, widest first. */
const widthsOf = (set: ImageSet): number[] =>
  Object.keys(set.jpeg).map(Number).filter((n) => Number.isFinite(n)).sort((a, b) => b - a);

const srcSet = (map: Record<string, string>, widths: number[]) =>
  widths.filter((w) => map[String(w)]).map((w) => `${map[String(w)]} ${w}w`).join(", ");

/** What a `<picture>` needs. Null when there is no image to show. */
export function pictureSources(v: StoredImage, sizes: string): {
  webpSrcSet: string | null;
  jpegSrcSet: string;
  src: string;
  width?: number;
  height?: number;
  sizes: string;
} | null {
  if (!v) return null;

  /* A legacy single URL still works: it becomes a one-entry set with no
   * srcset, which is exactly what it was before. An old setting must not
   * blank the hero the day this ships. */
  if (typeof v === "string") {
    return v.trim()
      ? { webpSrcSet: null, jpegSrcSet: "", src: v, sizes }
      : null;
  }
  if (!isImageSet(v)) return null;

  const widths = widthsOf(v);
  const smallest = widths[widths.length - 1];
  return {
    webpSrcSet: srcSet(v.webp ?? {}, widths) || null,
    jpegSrcSet: srcSet(v.jpeg, widths),
    /* `src` is the SMALLEST, not the largest: it is what a browser without
     * srcset support downloads, and the cost of it being too small somewhere
     * old is a soft picture, while the cost of it being too large is the very
     * thing this file exists to prevent. */
    src: v.jpeg[String(smallest)] ?? v.jpeg[String(widths[0])],
    width: v.width,
    height: v.height,
    sizes,
  };
}

/** Every URL a set points at — what «إزالة» would orphan in the bucket. */
export function urlsOf(v: StoredImage): string[] {
  if (!v) return [];
  if (typeof v === "string") return [v];
  if (!isImageSet(v)) return [];
  return [...Object.values(v.webp ?? {}), ...Object.values(v.jpeg)];
}
